import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { CurrentPortfolio } from 'src/app/models/current-portfolio.model';
import { Subscription } from 'rxjs';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MetricsAnalysis } from 'src/app/models/analysis/metrics-analysis.model';
import { ClustersService } from 'src/app/services/clusters.service';
import { Cluster } from 'src/app/models/cluster.model';
import { RiskManagementParameters } from 'src/app/models/risk-management-parameters.model';
import { RiskManagementService } from 'src/app/services/risk-management.service';
import { NotificationsService } from 'src/app/services/notifications.service';

@Component({
  selector: 'app-risk-management-insurance',
  templateUrl: './risk-management-insurance.component.html',
  styleUrls: ['./risk-management-insurance.component.scss']
})
export class RiskManagementInsuranceComponent implements OnInit, OnDestroy {

  currentCluster: Cluster;
  currentClusterSubscription: Subscription;

  insuranceMetrics: MetricsAnalysis;
  insuranceMetricsSubscription: Subscription;

  @Input() parameters;
  @Input() isComputing: boolean = false;
  @Input() showResults: boolean = false;

  investableMA: MetricsAnalysis;
  currentInvestableSubscription: Subscription;

  maxNbColumns: any = {
    one_year: 5,
    three_years: 5
  }

  selectedPage: string = 'cppi';
  selectedTab: string = 'Max Loss';
  computeEnabled: boolean = true;

  constructor(private clustersService: ClustersService,
              private riskManagementService: RiskManagementService,
              private notificationsService: NotificationsService) {}

  ngOnInit(): void {
    this.currentClusterSubscription = this.clustersService.currentClusterSubject.subscribe(
      (currentCluster: Cluster) => {
        this.currentCluster = currentCluster;
      }
    );
    this.clustersService.emitCurrentCluster();

    this.subscribeToCurrentInvestable();
    this.subscribeToInsuranceMetrics();

    // Default parameters if not specified by the parent component
    if (!this.parameters) {
      this.parameters = new RiskManagementParameters();
      this.parameters.initFromData({
        initial_capital: 100,
        aggressiveness: 'moderate',
        rebalancing_frequency: 'monthly',
        target_type: 'maximum_loss',
        protection_level: 10,
        risk_free_rate: 1,
        protection_horizon: ['one_year','three_years'][1]
      });
    }
  }

  ngOnDestroy() {
    if(this.currentClusterSubscription!=undefined) { this.currentClusterSubscription.unsubscribe(); }
    if(this.currentInvestableSubscription!=undefined) { this.currentInvestableSubscription.unsubscribe(); }
    if(this.insuranceMetricsSubscription!=undefined) { this.insuranceMetricsSubscription.unsubscribe(); }
  }

  subscribeToCurrentInvestable() {
    this.currentInvestableSubscription = this.clustersService.currentInvestableSubject.subscribe(
      (investable_portfolio: CurrentPortfolio) => {
        if (investable_portfolio) {
          this.investableMA = investable_portfolio.metricsAnalysis;
        }
      }
    );
    this.clustersService.emitCurrentInvestable();
  }

  subscribeToInsuranceMetrics() {
    this.insuranceMetricsSubscription = this.riskManagementService.insuranceMetricsSubject.subscribe(
      (metrics_analysis: MetricsAnalysis) => {
        if (metrics_analysis) {
          this.insuranceMetrics = metrics_analysis;
        }
      }
    );
    this.riskManagementService.emitInsuranceMetrics();
  }

  onTabChange(event: MatTabChangeEvent) {
    switch (event.tab.textLabel) {
      case 'Max Loss':      this.selectedTab='Max Loss'; break;
      case 'Max Drawdown':  this.selectedTab='Max Drawdown'; break;
      case 'Returns':       this.selectedTab='Returns'; break;
      case 'Volatility':    this.selectedTab='Volatility'; break;
    }
  }

  getFormattedPeriods(slice?: boolean, pivot?: string): string[] {
    if (slice==undefined) {slice = true;}
    if (pivot==undefined) {pivot = this.parameters.protection_horizon;}
    if (this.investableMA) {
      let last_periods;
      if (slice) {
        last_periods = this.investableMA[pivot].periods.slice(-this.maxNbColumns[this.parameters.protection_horizon]);
      } else {
        last_periods = this.investableMA[pivot].periods;
      }
      return last_periods.map(
        (period) => {
          const date_start:Date = new Date(period.start),
                date_end:Date = new Date(period.end);
          switch (this.parameters.protection_horizon) {
            case 'yearly':
              return date_end.getFullYear().toString();
            case 'one_year':
              return ('0'+(date_start.getMonth()+1)).slice(-2) + '/' + date_start.getFullYear().toString() + ' — ' +
                     ('0'+(date_end.getMonth()+1)).slice(-2) + '/' + date_end.getFullYear().toString();
            case 'three_years':
              return date_start.getFullYear().toString() + ' — ' + date_end.getFullYear().toString();
          }
        }
      );
    } else {
      return [];
    }
  }

  getPeriodValues(portfolio: string, metric: string, slice?: boolean, pivot?: string): number[] {
    if (slice==undefined) {slice = true;}
    if (pivot==undefined) {pivot = this.parameters.protection_horizon;}
    if (portfolio=='investable') {
      if (this.investableMA) {
        if (slice) {
          return this.investableMA[pivot][metric].slice(-this.maxNbColumns[pivot]);
        } else {
          return this.investableMA[pivot][metric];
        }
      } else {
        return [];
      }
    } else if (portfolio=='protected') {
      if (this.insuranceMetrics) {
        if (slice) {
          return this.insuranceMetrics[pivot][metric].slice(-this.maxNbColumns[pivot]);
        } else {
          return this.insuranceMetrics[pivot][metric];
        }
      } else {
        return [];
      }
    }
  }

  getFormattedPeriodValues(portfolio: string, metric: string, slice?: boolean, pivot?: string) {
    if (slice==undefined) {slice = true;}
    if (pivot==undefined) {pivot = this.parameters.protection_horizon;}
    let periods = this.getFormattedPeriods(slice),
        values = this.getPeriodValues(portfolio, metric, slice);

    let periodValues = {};
    periods.forEach(
      (period,index) => {
        periodValues[period] = values[index];
      }
    );

    return periodValues;
  }

  onParameterChange() {
    //console.log('CPARAMETERS HANGED');
    //console.log(this.parameters);
  }

  async onCompute() {
    this.notificationsService.runSpinner();
    this.isComputing = true;
    this.showResults = false;

    try {
      await this.riskManagementService.computeInsurance(this.parameters, this.currentCluster);
      this.notificationsService.notify('computation-success');
      this.showResults = true;
    }
    catch(e) {
      this.notificationsService.notify('computation-failed');
      this.showResults = false;
    }

    this.isComputing = false;
    this.notificationsService.stopSpinner();
  }
}
